/**
 * Shop This Look Custom Element
 * Handles product recommendations with complementary intent
 * and provides curated shopping experiences
 */

class ShopThisLook extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    
    // Use Intersection Observer for lazy initialization (performance optimization)
    if ('IntersectionObserver' in window) {
      this.setupLazyInit();
    } else {
      // Fallback for older browsers - initialize after a delay
      setTimeout(() => this.init(), 100);
    }
  }

  setupLazyInit() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.initialized) {
          this.initialized = true;
          observer.disconnect(); // Stop observing after initialization
          this.init();
        }
      });
    }, {
      rootMargin: '200px', // Start loading 200px before element enters viewport
      threshold: 0.01
    });
    
    observer.observe(this);
  }

  init() {
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.intent = this.dataset.intent || 'complementary';
    this.url = this.dataset.url;
    this.limit = parseInt(this.dataset.limit) || 12;
    this.productsPerRow = parseInt(this.dataset.productsPerRow) || 4;
    this.boxCount = parseInt(this.dataset.productsCount) || 4;
    this.enable = this.dataset.enable === 'true';
    this.useStorefrontAPI = this.dataset.useStorefrontApi === 'true'; // New flag for Storefront API
    this.excludeSaleProducts = this.dataset.excludeSaleProducts === 'true'; // Exclude sale products setting
    
    // Storefront API configuration
    this.STOREFRONT_API_TOKEN = '0cbe221a4b97fa2aef38fe588fab668e';
    this.STOREFRONT_API_VERSION = '2024-01';
    
    this.container = this.querySelector('.shop-this-look__recommendations-container');
    this.placeholder = this.querySelector('.shop-this-look-placeholder');
    
    this.complementaryProducts = [];
    this.selectedProducts = new Set();
    
    // Load fallback data from server-side JSON (ALWAYS - this is the safety net)
    this.loadFallbackData();
    
    // Try Storefront API enhancement if enabled (for large product pool)
    if (this.useStorefrontAPI) {
      this.enhanceWithStorefrontAPI();
    }
    
    // ALSO enhance with Product Recommendations API if enabled (for complementary intelligence)
    if (this.enable) {
      this.fetchRecommendations();
    }
    
    this.bindEvents();
  }

  loadFallbackData() {
    try {
      const fallbackScript = document.getElementById(`shop-this-look-fallback-${this.sectionId}`);
      if (!fallbackScript) {
        console.log('⚠️ No fallback data found, using placeholder products');
        this.renderProducts();
        this.hidePlaceholder();
        return;
      }
      
      const fallbackData = JSON.parse(fallbackScript.textContent);
      
      // Set current product from server data
      this.currentProduct = fallbackData.currentProduct;
      
      // Set popup settings
      this.popupSettings = fallbackData.popupSettings || { enabled: false };
      
      // Set complementary products from structured server data
      this.fallbackProductsByCategory = fallbackData.fallbackProducts || {};
      
      // Initialize categorized products with fallback data
      this.categorizedProducts = {
        top: fallbackData.fallbackProducts?.top || [],
        outerwear: fallbackData.fallbackProducts?.outerwear || [],
        bottom: fallbackData.fallbackProducts?.bottom || [],
        dress: fallbackData.fallbackProducts?.dress || [],
        handbag: fallbackData.fallbackProducts?.handbag || [],
        footwear: fallbackData.fallbackProducts?.footwear || []
      };
      
      // Store fallback data for later use
      this.fallbackData = fallbackData;
      
      // Add current product to its appropriate category
      this.addCurrentProductToCategory();
      
      // Render immediately with fallback data
      this.renderProducts();
      
      // Hide placeholder after a short delay to ensure smooth transition
      setTimeout(() => {
        this.hidePlaceholder();
      }, 100);
      
    } catch (error) {
      console.error('❌ Error loading fallback data:', error);
      this.complementaryProducts = [];
      this.renderProducts(); // Still show with placeholders
      this.hidePlaceholder();
    }
  }

  addCurrentProductToCategory() {
    // Simple: just add the current product to its appropriate category
    const currentProduct = this.fallbackData?.currentProduct;
    
    if (currentProduct) {
      const currentCategory = currentProduct._category || currentProduct.category;
      
      // Add current product to the front of its appropriate category
      if (currentCategory === 'top') {
        this.categorizedProducts.top = this.categorizedProducts.top || [];
        this.categorizedProducts.top.unshift(currentProduct);
      } else if (currentCategory === 'outerwear') {
        this.categorizedProducts.outerwear = this.categorizedProducts.outerwear || [];
        this.categorizedProducts.outerwear.unshift(currentProduct);
      } else if (currentCategory === 'bottom') {
        this.categorizedProducts.bottom = this.categorizedProducts.bottom || [];
        this.categorizedProducts.bottom.unshift(currentProduct);
      } else if (currentCategory === 'dress') {
        this.categorizedProducts.dress = this.categorizedProducts.dress || [];
        this.categorizedProducts.dress.unshift(currentProduct);
      } else if (currentCategory === 'handbag') {
        this.categorizedProducts.handbag = this.categorizedProducts.handbag || [];
        this.categorizedProducts.handbag.unshift(currentProduct);
      } else if (currentCategory === 'footwear') {
        this.categorizedProducts.footwear = this.categorizedProducts.footwear || [];
        this.categorizedProducts.footwear.unshift(currentProduct);
      }
    }
  }

  calculateMaxBoxes() {
    const outerwear = this.categorizedProducts?.outerwear || [];
    const bottom = this.categorizedProducts?.bottom || [];
    const handbag = this.categorizedProducts?.handbag || [];
    const footwear = this.categorizedProducts?.footwear || [];
    
    // Calculate how many complete boxes we can create
    const minProductsPerCategory = Math.min(
      outerwear.length,
      bottom.length, 
      handbag.length,
      footwear.length
    );
    
    return Math.min(minProductsPerCategory, this.boxCount);
  }

  mergeApiWithFallback(categorizedApiProducts) {
    // Get existing products (already includes Storefront + fallback if they ran first)
    const existingProducts = this.categorizedProducts || {};
    
    
    // Merge: Product Recs FIRST (intelligent), then existing (Storefront + fallback)
    const mergedProducts = {
      top: [...(categorizedApiProducts.top || []), ...(existingProducts.top || [])],
      outerwear: [...(categorizedApiProducts.outerwear || []), ...(existingProducts.outerwear || [])],
      bottom: [...(categorizedApiProducts.bottom || []), ...(existingProducts.bottom || [])],
      dress: [...(categorizedApiProducts.dress || []), ...(existingProducts.dress || [])],
      handbag: [...(categorizedApiProducts.handbag || []), ...(existingProducts.handbag || [])],
      footwear: [...(categorizedApiProducts.footwear || []), ...(existingProducts.footwear || [])]
    };
    
    // Deduplicate by product ID (keeps first occurrence = Product Recs)
    Object.keys(mergedProducts).forEach(category => {
      const seen = new Set();
      mergedProducts[category] = mergedProducts[category].filter(product => {
        if (seen.has(product.id)) return false;
        seen.add(product.id);
        return true;
      });
    });
    
    this.categorizedProducts = mergedProducts;
    
    // Store how many Product Recommendations we have (for box selection logic)
    this.recommendationsCounts = {
      top: categorizedApiProducts.top?.length || 0,
      outerwear: categorizedApiProducts.outerwear?.length || 0,
      bottom: categorizedApiProducts.bottom?.length || 0,
      dress: categorizedApiProducts.dress?.length || 0,
      handbag: categorizedApiProducts.handbag?.length || 0,
      footwear: categorizedApiProducts.footwear?.length || 0
    };
  }

  async fetchRecommendations() {
    try {
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(this.url, { 
        signal: controller.signal 
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) throw new Error('Failed to fetch recommendations');
      
      const jsonData = await response.json();
      
      // Extract API product data from JSON response
      const apiProducts = this.extractProductDataFromJSON(jsonData);
      
      // Only use API if we have meaningful results
      if (apiProducts.length >= 1) {
        // Categorize API products using corrected logic (dress-first approach)
        const categorizedApiProducts = this.categorizeApiProducts(apiProducts);
        
        // Merge API products with existing fallback products to ensure we have enough for all boxes
        this.mergeApiWithFallback(categorizedApiProducts);
        
        // Re-render with API products
        this.renderProducts();
      }
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('⚠️ Product Recommendations API failed:', error);
      }
      // Fallback data is already loaded and rendered - no action needed
    }
  }

  extractProductDataFromJSON(jsonData) {
    const products = jsonData.products || [];
    const apiProducts = [];
    
    products.forEach((product, index) => {
      if (index >= this.limit) return;
      
      // Skip out-of-stock products
      if (product.available === false) return;
      
      const productData = this.parseProductFromJSON(product);
      if (productData) {
        apiProducts.push(productData);
      }
    });
    
    return apiProducts;
  }

  parseProductFromJSON(product) {
    try {
      // Extract collections and tags from product data
      const collections = product.collections ? product.collections.map(c => c.handle) : [];
      const tags = product.tags ? product.tags.join(',') : '';
      
      return {
        id: product.id.toString(),
        title: product.title || 'Untitled Product',
        price: this.formatPrice(product.price),
        image: product.featured_image ? `https:${product.featured_image}` : '',
        imageAlt: product.title || '',
        url: product.url || '#',
        vendor: product.vendor || '',
        collections: collections,
        tags: tags
      };
    } catch (error) {
      console.error('Error parsing product from JSON:', error);
      return null;
    }
  }

  formatPrice(priceInCents) {
    return `$${(priceInCents / 100).toFixed(2)}`;
  }

  categorizeApiProducts(apiProducts) {
    // Categorize API products using similar logic to Liquid template
    const categorized = {
      top: [],
      outerwear: [],
      bottom: [],
      dress: [],
      handbag: [],
      footwear: []
    };
    
    apiProducts.forEach(product => {
      // Skip if it's the current product (compare as strings to handle different formats)
      if (product.id.toString() === this.currentProduct?.id?.toString()) {
        return;
      }
      
      // Skip sale products if setting is enabled
      if (this.excludeSaleProducts && product.tags && product.tags.toLowerCase().includes('sale')) {
        return;
      }
      
      // Extract collections and tags from product data with defensive checks
      const collections = Array.isArray(product.collections) ? product.collections : [];
      const tags = (typeof product.tags === 'string' ? product.tags : '').toLowerCase();
      
      
      // Categorize based on collections and tags (similar to Liquid logic)
      let category = null;
      
      try {
        // Check for DRESS FIRST to prevent miscategorization
        if ((product.handle && product.handle.includes('dress')) || 
            (product.title && product.title.toLowerCase().includes('dress')) || 
            tags.includes('dress') || collections.includes('dresses')) {
          category = 'dress';
        }
      // Check for footwear  
      else if (collections.includes('footwear') || tags.includes('footwear') || 
          tags.includes('shoe') || tags.includes('boot') || tags.includes('sandal') || 
          tags.includes('sneaker') || tags.includes('heel')) {
        category = 'footwear';
      } 
      // Check for handbags (AFTER dress check)
      else if (collections.includes('handbags') || tags.includes('handbag') || 
               tags.includes('bag') || tags.includes('purse') || tags.includes('clutch') ||
               tags.includes('tote') || tags.includes('crossbody')) {
        category = 'handbag';
      } 
      // Check for outerwear (blazers, jackets, sweaters)
      else if (collections.includes('sweaters') || collections.includes('jackets') || 
               tags.includes('jacket') || tags.includes('blazer') || tags.includes('sweater') || 
               tags.includes('outerwear') || tags.includes('coat') || tags.includes('cardigan') ||
               tags.includes('blazers')) {
        category = 'outerwear';
      } 
      // Check for bottoms (pants, skirts, denim)
      else if (collections.includes('denim') || collections.includes('bottoms') || 
               tags.includes('denim') || tags.includes('jean') || tags.includes('pant') || 
               tags.includes('bottom') || tags.includes('skirt') || tags.includes('trouser') ||
               tags.includes('legging')) {
        category = 'bottom';
      }
      // Check for tops (shirts, blouses, tanks)
      else if (collections.includes('tops') || collections.includes('blouses') || 
               tags.includes('top') || tags.includes('blouse') || tags.includes('shirt') || 
               tags.includes('tank') || tags.includes('tee') || tags.includes('cami') ||
               tags.includes('crop')) {
        category = 'top';
      }
      
        // Add to appropriate category if found
        if (category && categorized[category]) {
          categorized[category].push(product);
        }
        
      } catch (error) {
        console.error(`❌ Error categorizing API product "${product.title}":`, error);
      }
    });
    
    return categorized;
  }

  parseProductElement(element) {
    try {
      // Extract product information using theme-specific selectors
      const titleElement = element.querySelector('.grid-product__title');
      const priceElement = element.querySelector('.grid-product__price');
      const imageElement = element.querySelector('.grid-product__image, img');
      const linkElement = element.querySelector('.grid-product__link, a');
      
      // Extract collections and tags from data attributes or other elements
      const collections = element.dataset.collections ? element.dataset.collections.split(',') : [];
      const tags = element.dataset.tags || '';
      
      return {
        title: titleElement?.textContent?.trim() || element.dataset.productTitle || 'Untitled Product',
        price: priceElement?.textContent?.trim() || '',
        image: imageElement?.src || imageElement?.dataset?.src || '',
        imageAlt: imageElement?.alt || '',
        url: linkElement?.href || '#',
        vendor: element.dataset.vendor || '',
        collections: collections,
        tags: tags
      };
    } catch (error) {
      console.error('Error parsing product element:', error);
      return null;
    }
  }

  renderProducts() {
    const collages = [];
    
    // Get current product info for special handling
    const currentProduct = this.fallbackData?.currentProduct;
    const currentCategory = currentProduct?._category || currentProduct?.category;
    
    // Get all category arrays once
    const tops = this.categorizedProducts?.top || [];
    const dress = this.categorizedProducts?.dress || [];
    const outerwear = this.categorizedProducts?.outerwear || [];
    const bottom = this.categorizedProducts?.bottom || [];
    const handbag = this.categorizedProducts?.handbag || [];
    const footwear = this.categorizedProducts?.footwear || [];
    
    // Add random offsets for variety on each page load
    // For Storefront API: same-vendor products are at front, all vendors after
    // Use actual same-vendor counts if available, otherwise fallback to array length
    const sameVendorCounts = this.sameVendorCounts || {};
    
    const topOffset = Math.floor(Math.random() * Math.max(1, tops.length));
    const topOffsetBox0 = Math.floor(Math.random() * Math.max(1, sameVendorCounts.top || tops.length));
    
    const dressOffset = Math.floor(Math.random() * Math.max(1, dress.length));
    const dressOffsetBox0 = Math.floor(Math.random() * Math.max(1, sameVendorCounts.dress || dress.length));
    
    const outerwearOffset = Math.floor(Math.random() * Math.max(1, outerwear.length));
    const outerwearOffsetBox0 = Math.floor(Math.random() * Math.max(1, sameVendorCounts.outerwear || outerwear.length));
    
    const bottomOffset = Math.floor(Math.random() * Math.max(1, bottom.length));
    const bottomOffsetBox0 = Math.floor(Math.random() * Math.max(1, sameVendorCounts.bottom || bottom.length));
    
    const handbagOffset = Math.floor(Math.random() * Math.max(1, handbag.length));
    const footwearOffset = Math.floor(Math.random() * Math.max(1, footwear.length));
    
    for (let i = 0; i < this.boxCount; i++) {
      // Determine path: dress path vs separates path
      let useDressPath = false;
      
      // If current product is a dress, ALWAYS use dress path
      if (currentCategory === 'dress') {
        useDressPath = true;
      }
      // If current product is outerwear, alternate between paths for variety
      else if (currentCategory === 'outerwear' && dress.length > 0) {
        // Boxes 1&3 use dress path, boxes 0&2 use separates path
        useDressPath = (i === 1 || i === 3);
      }
      
      const collageProducts = [];
      
      // Position 0: Tops
      if (currentCategory === 'top') {
        // Current product appears in ALL boxes
        collageProducts[0] = currentProduct;
      } else if (!useDressPath && tops.length > 0) {
        // If we have Product Recommendations, use them directly (no randomization)
        const recsCount = this.recommendationsCounts?.top || 0;
        if (i < recsCount) {
          // Use Product Recommendation directly (index 0-3 for boxes 0-3)
          collageProducts[0] = tops[i];
        } else {
          // Use Storefront API with randomization
          const hasSameVendor = sameVendorCounts.top && sameVendorCounts.top > 0;
          const offset = i === 0 ? (hasSameVendor ? topOffsetBox0 : topOffset) : topOffset;
          collageProducts[0] = tops[(i * 17 + offset) % tops.length];
        }
      } else if (!useDressPath) {
        collageProducts[0] = this.createPlaceholder('Top', i, 0);
      }
      // Position 0 is empty for dress path
      
      // Position 1: Dress - Center (replaces tops when dress is present)
      if (currentCategory === 'dress') {
        // Current product appears in ALL boxes
        collageProducts[1] = currentProduct;
      } else if (useDressPath && dress.length > 0) {
        // If we have Product Recommendations, use them directly (no randomization)
        const recsCount = this.recommendationsCounts?.dress || 0;
        if (i < recsCount) {
          collageProducts[1] = dress[i];
        } else {
          // Use Storefront API with randomization
          const hasSameVendor = sameVendorCounts.dress && sameVendorCounts.dress > 0;
          const offset = i === 0 ? (hasSameVendor ? dressOffsetBox0 : dressOffset) : dressOffset;
          collageProducts[1] = dress[(i * 17 + offset) % dress.length];
        }
      } else if (useDressPath) {
        collageProducts[1] = this.createPlaceholder('Dress', i, 1);
      }
      // Position 1 stays empty for separates path
      
      // Position 2: Outerwear
      if (currentCategory === 'outerwear') {
        // Current product appears in ALL boxes
        collageProducts[2] = currentProduct;
      } else if (outerwear.length > 0) {
        // If we have Product Recommendations, use them directly (no randomization)
        const recsCount = this.recommendationsCounts?.outerwear || 0;
        if (i < recsCount) {
          collageProducts[2] = outerwear[i];
        } else {
          // Use Storefront API with randomization
          const hasSameVendor = sameVendorCounts.outerwear && sameVendorCounts.outerwear > 0;
          const offset = i === 0 ? (hasSameVendor ? outerwearOffsetBox0 : outerwearOffset) : outerwearOffset;
          collageProducts[2] = outerwear[(i * 17 + offset) % outerwear.length];
        }
      } else {
        collageProducts[2] = this.createPlaceholder('Outerwear', i, 2);
      }
      
      // Position 3: Bottom (only for separates path, empty for dress path)
      if (!useDressPath) {
        // Separates path: include bottoms
        if (currentCategory === 'bottom') {
          // Current product appears in ALL boxes
          collageProducts[3] = currentProduct;
        } else if (bottom.length > 0) {
          // If we have Product Recommendations, use them directly (no randomization)
          const recsCount = this.recommendationsCounts?.bottom || 0;
          if (i < recsCount) {
            collageProducts[3] = bottom[i];
          } else {
            // Use Storefront API with randomization
            const hasSameVendor = sameVendorCounts.bottom && sameVendorCounts.bottom > 0;
            const offset = i === 0 ? (hasSameVendor ? bottomOffsetBox0 : bottomOffset) : bottomOffset;
            collageProducts[3] = bottom[(i * 17 + offset) % bottom.length];
          }
        } else {
          collageProducts[3] = this.createPlaceholder('Bottom', i, 3);
        }
      }
      // For dress path: position 3 stays empty (no bottoms with dresses)
      
      // Position 4: Handbags
      if (currentCategory === 'handbag' && i === 0) {
        // Current handbag only in box 0 (other boxes show variety)
        collageProducts[4] = currentProduct;
      } else if (currentCategory === 'handbag' && handbag.length > 0) {
        // Boxes 1-3: Show different handbags for variety
        collageProducts[4] = handbag[(handbagOffset + i) % handbag.length];
      } else if (handbag.length > 0) {
        // If we have Product Recommendations, use them directly (no randomization)
        const recsCount = this.recommendationsCounts?.handbag || 0;
        if (i < recsCount) {
          collageProducts[4] = handbag[i];
        } else {
          // Use offset + i for guaranteed different products per box
          collageProducts[4] = handbag[(handbagOffset + i) % handbag.length];
        }
      } else {
        collageProducts[4] = this.createPlaceholder('Handbag', i, 4);
      }
      
      // Position 5: Footwear
      if (currentCategory === 'footwear' && i === 0) {
        // Current footwear only in box 0 (other boxes show variety)
        collageProducts[5] = currentProduct;
      } else if (currentCategory === 'footwear' && footwear.length > 0) {
        // Boxes 1-3: Show different footwear for variety
        collageProducts[5] = footwear[(footwearOffset + i) % footwear.length];
      } else if (footwear.length > 0) {
        // If we have Product Recommendations, use them directly (no randomization)
        const recsCount = this.recommendationsCounts?.footwear || 0;
        if (i < recsCount) {
          collageProducts[5] = footwear[i];
        } else {
          // Use offset + i for guaranteed different products per box
          collageProducts[5] = footwear[(footwearOffset + i) % footwear.length];
        }
      } else {
        collageProducts[5] = this.createPlaceholder('Footwear', i, 5);
      }
      
      collages.push(collageProducts);
    }
    
    // Store collages for popup to use exact same data
    this.builtCollages = collages;
    
    const gridHTML = `
      <div class="shop-this-look__grid shop-this-look__grid--${this.productsPerRow}-per-row">
        ${collages.map((collage, index) => this.renderCollageBox(collage, index)).join('')}
      </div>
    `;
    
    this.container.innerHTML = gridHTML;
    this.bindProductEvents();
    
    // Preload images for better UX
    this.preloadImages();
  }

  renderOldSystem() {
    // Use the original working category-based system
    
    // Get current product info for special handling
    const currentProduct = this.fallbackData?.currentProduct;
    const currentCategory = currentProduct?._category || currentProduct?.category;
    
    const collages = [];
    
    for (let i = 0; i < this.boxCount; i++) {
      const collageProducts = [];
      
      // Position 0: Tops
      const tops = this.categorizedProducts?.top || [];
      if (currentCategory === 'top') {
        collageProducts[0] = currentProduct;
      } else if (i === 0) {
        collageProducts[0] = tops[0] || this.createPlaceholder('Top', i, 0);
      } else if (tops.length > i) {
        collageProducts[0] = tops[i];
      } else {
        collageProducts[0] = this.createPlaceholder('Top', i, 0);
      }
      
      // Position 2: Outerwear
      const outerwear = this.categorizedProducts?.outerwear || [];
      if (currentCategory === 'outerwear') {
        collageProducts[2] = currentProduct;
      } else if (i === 0) {
        collageProducts[2] = outerwear[0] || this.createPlaceholder('Outerwear', i, 2);
      } else if (outerwear.length > i) {
        collageProducts[2] = outerwear[i];
      } else {
        collageProducts[2] = this.createPlaceholder('Outerwear', i, 2);
      }
      
      // Position 3: Bottom
      const bottom = this.categorizedProducts?.bottom || [];
      if (currentCategory === 'bottom') {
        collageProducts[3] = currentProduct;
      } else if (i === 0) {
        collageProducts[3] = bottom[0] || this.createPlaceholder('Bottom', i, 3);
      } else if (bottom.length > i) {
        collageProducts[3] = bottom[i];
      } else {
        collageProducts[3] = this.createPlaceholder('Bottom', i, 3);
      }
      
      // Position 4: Handbags
      const handbag = this.categorizedProducts?.handbag || [];
      if (currentCategory === 'handbag') {
        collageProducts[4] = currentProduct;
      } else if (handbag.length > 0) {
        collageProducts[4] = handbag[i % handbag.length];
      } else {
        collageProducts[4] = this.createPlaceholder('Handbag', i, 4);
      }
      
      // Position 5: Footwear
      const footwear = this.categorizedProducts?.footwear || [];
      if (currentCategory === 'footwear') {
        collageProducts[5] = currentProduct;
      } else if (footwear.length > 0) {
        const footwearIndex = (i + 2) % footwear.length;
        collageProducts[5] = footwear[footwearIndex];
      } else {
        collageProducts[5] = this.createPlaceholder('Footwear', i, 5);
      }
      
      collages.push(collageProducts);
    }
    
    const gridHTML = `
      <div class="shop-this-look__grid shop-this-look__grid--${this.productsPerRow}-per-row">
        ${collages.map((collage, index) => this.renderCollageBox(collage, index)).join('')}
      </div>
    `;
    
    this.container.innerHTML = gridHTML;
    this.bindProductEvents();
    this.preloadImages();
  }

  renderCollageBox(collageProducts, boxIndex) {
    // Determine which product is the current product for z-index handling
    const currentProductId = this.currentProduct?.id?.toString();
    
    return `
      <div class="shop-this-look__item" 
           data-box-index="${boxIndex}"
           role="button"
           tabindex="0"
           aria-label="Shop Look ${boxIndex + 1}">
        <div class="shop-this-look__collage">
          ${collageProducts.map((product, arrayIndex) => {
            // Render ALL positions, even empty ones (CSS handles display)
            if (!product || !product.title || product.title === 'Empty') {
              return `<div class="shop-this-look__collage-product shop-this-look__collage-product--${arrayIndex}" style="display:none;"></div>`;
            }
            
            // Check if this is the current product and give it elevated z-index
            const isCurrentProduct = product.id?.toString() === currentProductId;
            const zIndexStyle = isCurrentProduct ? ' style="z-index: 10;"' : '';
            
            // Add scaling classes for specific product types
            let scalingClass = '';
            const tags = product.tags || '';
            
            // Maxi dresses get scaling
            if (tags.includes('dresses - maxi')) {
              scalingClass = ' shop-this-look__collage-product--scaled';
            }
            // Long jackets get scaling
            else if (tags.includes('jackets - long')) {
              scalingClass = ' shop-this-look__collage-product--scaled';
            }
            
            return `
              <div class="shop-this-look__collage-product shop-this-look__collage-product--${arrayIndex}${scalingClass}"${zIndexStyle}
                   data-product-id="${product.id}"
                   data-is-current="${isCurrentProduct}"
                   tabindex="0"
                   role="button"
                   aria-label="Shop ${product.title}">
                <div class="shop-this-look__image-wrapper">
                  <img class="shop-this-look__collage-image" 
                       src="${product.image}" 
                       alt="${product.imageAlt || product.title}"
                       loading="lazy"
                       onload="this.parentElement.classList.add('loaded')"
                       onerror="this.parentElement.classList.add('error'); this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjVGNUY1Bi8+CjxwYXRoIGQ9Ik0xNzUgMTUwSDIyNVYyNTBIMTc1VjE1MFoiIGZpbGw9IiNEOUQ5RDkiLz4KPHN2Zz4K'">
                  <div class="shop-this-look__image-loading"></div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
        <div class="shop-this-look__content">
          <a href="#" class="shop-this-look__view-details" 
             data-box-index="${boxIndex}"
             onclick="event.preventDefault(); return false;">
            View Details
          </a>
        </div>
      </div>
    `;
  }

  bindProductEvents() {
    // Individual product container clicks
    const productContainers = this.querySelectorAll('.shop-this-look__collage-product');
    
    productContainers.forEach(container => {
      const productId = container.dataset.productId;
      
      // Remove individual product click prevention to allow bubbling to look container
      container.addEventListener('click', (e) => {
        // Allow event to bubble to look container for popup
      });
      
      // Handle keyboard navigation on containers - now handled by look container
      container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          // Keyboard navigation now handled by look container
        }
      });
    });
    
    // Container clicks for overall look interaction
    const lookItems = this.querySelectorAll('.shop-this-look__item');
    
    lookItems.forEach(item => {
      const boxIndex = item.dataset.boxIndex;
      
      // Handle look container clicks - show complete look popup
      item.addEventListener('click', (e) => {
        // Show complete look popup if enabled
        if (this.popupSettings && this.popupSettings.enabled) {
          this.showCompleteLookPopup(parseInt(boxIndex), item);
        }
      });
    });

    // Handle "View Details" link clicks 
    const viewDetailsLinks = this.querySelectorAll('.shop-this-look__view-details');
    
    viewDetailsLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const boxIndex = parseInt(link.dataset.boxIndex);
        const container = link.closest('.shop-this-look__item');
        
        // Show complete look popup if enabled
        if (this.popupSettings && this.popupSettings.enabled) {
          this.showCompleteLookPopup(boxIndex, container);
        }
      });
    });
  }

  bindEvents() {
    // Popup close functionality
    if (this.popupSettings && this.popupSettings.enabled) {
      const popup = document.getElementById(`shop-this-look-popup-${this.popupSettings.sectionId}`);
      if (popup) {
        // Close button
        const closeButton = popup.querySelector('.shop-this-look__popup-close');
        if (closeButton) {
          closeButton.addEventListener('click', () => this.hideCompleteLookPopup());
        }
        
        // Overlay click to close
        popup.addEventListener('click', (e) => {
          if (e.target === popup) {
            this.hideCompleteLookPopup();
          }
        });
        
        // Escape key to close
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && popup.style.display !== 'none') {
            this.hideCompleteLookPopup();
          }
        });
      }
    }
  }

  preloadImages() {
    const images = this.container.querySelectorAll('.shop-this-look__collage-image');
    let loadedCount = 0;
    const totalImages = images.length;
    
    if (totalImages === 0) {
      this.hidePlaceholder();
      return;
    }
    
    // Set a timeout to hide placeholder after reasonable time (3 seconds)
    const timeoutId = setTimeout(() => {
      this.hidePlaceholder();
    }, 3000);
    
    images.forEach(img => {
      if (img.complete) {
        loadedCount++;
        if (loadedCount === totalImages) {
          clearTimeout(timeoutId);
          this.hidePlaceholder();
        }
      } else {
        img.addEventListener('load', () => {
          loadedCount++;
          if (loadedCount === totalImages) {
            clearTimeout(timeoutId);
            this.hidePlaceholder();
          }
        });
        
        img.addEventListener('error', () => {
          loadedCount++;
          if (loadedCount === totalImages) {
            clearTimeout(timeoutId);
            this.hidePlaceholder();
          }
        });
      }
    });
  }

  hidePlaceholder() {
    if (this.placeholder) {
      // Add a class for smooth transition
      this.placeholder.classList.add('fade-out');
      setTimeout(() => {
        this.placeholder.style.display = 'none';
      }, 300);
    }
  }

  showEmptyState() {
    this.container.innerHTML = `
      <div class="shop-this-look__empty">
        <p>No recommendations available at the moment.</p>
      </div>
    `;
    this.hidePlaceholder();
  }

  createPlaceholder(category, boxIndex, position) {
    // Use inline SVG data URI instead of external placeholder service
    const placeholderSVG = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Crect width='300' height='300' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='16' fill='%23999'%3E${category}%3C/text%3E%3C/svg%3E`;
    
    return {
      id: `placeholder-${boxIndex}-${position}`,
      title: category,
      price: '$99.00',
      image: placeholderSVG,
      imageAlt: category,
      url: '#',
      vendor: 'Shop',
      category: category.toLowerCase()
    };
  }

  updateCartCount() {
    // Trigger cart count update if theme has this functionality
    if (window.theme && window.theme.updateCartCount) {
      window.theme.updateCartCount();
    }
    
    // Dispatch custom event for other cart listeners
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  showCompleteLookPopup(boxIndex, clickedContainer) {
    const popup = document.getElementById(`shop-this-look-popup-${this.popupSettings.sectionId}`);
    if (!popup) return;

    const popupContainer = popup.querySelector('.shop-this-look__popup-container');
    const productsGrid = popup.querySelector('.shop-this-look__popup-products-grid');
    if (!productsGrid || !popupContainer) return;

    // Position popup
    const viewportWidth = window.innerWidth;
    if (viewportWidth > 768) {
      const containerRect = clickedContainer.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const popupWidth = 400;

      let left = containerRect.right + 20;
      let top = containerRect.top;

      if (left + popupWidth > viewportWidth - 20) {
        left = containerRect.left - popupWidth - 20;
      }
      if (left < 20) {
        left = (viewportWidth - popupWidth) / 2;
      }
      if (top + 400 > viewportHeight - 20) {
        top = viewportHeight - 400 - 20;
      }
      if (top < 20) {
        top = 20;
      }

      popupContainer.style.left = left + 'px';
      popupContainer.style.top = top + 'px';
    } else {
      popupContainer.style.left = '';
      popupContainer.style.top = '';
    }

    // Read products directly from the DOM box that was clicked - NO separate logic!
    const boxElement = clickedContainer.closest('.shop-this-look__item');
    const productElements = boxElement.querySelectorAll('.shop-this-look__collage-product');
    
    const visualProducts = [];
    productElements.forEach(productEl => {
      const productId = productEl.dataset.productId;
      
      // Find this product in our categorized data
      const allCategoryProducts = [
        ...(this.categorizedProducts?.top || []),
        ...(this.categorizedProducts?.dress || []),
        ...(this.categorizedProducts?.outerwear || []),
        ...(this.categorizedProducts?.bottom || []),
        ...(this.categorizedProducts?.handbag || []),
        ...(this.categorizedProducts?.footwear || []),
        this.fallbackData?.currentProduct
      ].filter(Boolean);
      
      const foundProduct = allCategoryProducts.find(p => p.id.toString() === productId);
      if (foundProduct) {
        visualProducts.push(foundProduct);
      }
    });

    // Generate HTML for products list (horizontal rows)
    const productsHTML = visualProducts.map((product, index) => {
      const isCurrentProduct = product.id.toString() === this.currentProduct.id.toString();
      
      // Add tracking parameters to product URL
      const separator = product.url.includes('?') ? '&' : '?';
      const trackedUrl = `${product.url}${separator}ref=shop-this-look&box=${boxIndex}&pos=${index}`;
      
      return `
        <div class="shop-this-look__popup-product ${isCurrentProduct ? 'shop-this-look__popup-product--current' : ''}"
             data-product-id="${product.id}">
          <div class="shop-this-look__popup-product-image-wrapper">
            <img class="shop-this-look__popup-product-image" 
                 src="${product.image}" 
                 alt="${product.imageAlt || product.title}"
                 width="80" 
                 height="80" 
                 loading="lazy">
          </div>
          <div class="shop-this-look__popup-product-content">
            <div class="shop-this-look__popup-product-details">
              <p class="shop-this-look__popup-product-vendor">${product.vendor}</p>
              <h4 class="shop-this-look__popup-product-title">${product.title}</h4>
              <p class="shop-this-look__popup-product-price">${product.price}</p>
            </div>
            <a class="shop-this-look__popup-product-button" 
               href="${trackedUrl}" 
               target="_blank" 
               rel="noopener noreferrer"
               data-product-id="${product.id}"
               data-product-title="${product.title}"
               data-product-vendor="${product.vendor}"
               data-product-price="${product.price}"
               data-position="${index}"
               onclick="window.shopThisLookInstance?.trackProductClick(event, this, ${boxIndex})">
              ${this.popupSettings.buttonText || 'View Product'}
            </a>
          </div>
        </div>
      `;
    }).join('');

    // Update popup content
    productsGrid.innerHTML = productsHTML;

    // Show popup with slide-in animation
    popup.style.display = 'flex';
    
    // Force reflow before adding animation class
    popup.offsetHeight;
    
    popup.classList.add('show');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Track look view with GA4
    this.trackLookView(boxIndex, visualProducts);
  }

  hideCompleteLookPopup() {
    const popup = document.getElementById(`shop-this-look-popup-${this.popupSettings.sectionId}`);
    if (!popup) return;

    popup.classList.remove('show');
    
    // Wait for transition to complete before hiding
    setTimeout(() => {
      popup.style.display = 'none';
    }, 300);
    
    // Restore body scroll
    document.body.style.overflow = '';
  }

  // ============================================================================
  // STOREFRONT API METHODS
  // ============================================================================

  /**
   * Enhance product recommendations using Shopify Storefront API
   * Runs in parallel with existing fallback data (non-breaking)
   */
  async enhanceWithStorefrontAPI() {
    try {
      const currentProduct = this.fallbackData?.currentProduct;
      const currentVendor = currentProduct?.vendor || '';
      const currentCategory = currentProduct?._category || currentProduct?.category;
      
      // Fetch products in TWO batches: same vendor (for box 0) + all vendors (for variety)
      // This matches the original Liquid logic: Pass 1 (Rails/same vendor), Pass 2 (All vendors)
      
      const [
        topsSameVendor, outerwearSameVendor, bottomsSameVendor, dressesSameVendor,
        topsAll, outerwearAll, bottomsAll, dressesAll, handbags, footwear
      ] = await Promise.all([
        // Same vendor products (for box 0 priority)
        this.queryStorefrontAPI('tops', currentVendor),
        this.queryStorefrontAPI('outerwear', currentVendor),
        this.queryStorefrontAPI('bottoms', currentVendor),
        this.queryStorefrontAPI('dresses', currentVendor),
        // All vendor products (for variety in boxes 1-3)
        this.queryStorefrontAPI('tops', ''),
        this.queryStorefrontAPI('outerwear', ''),
        this.queryStorefrontAPI('bottoms', ''),
        this.queryStorefrontAPI('dresses', ''),
        // Accessories (all vendors always)
        this.queryStorefrontAPI('handbags', ''),
        this.queryStorefrontAPI('footwear', '')
      ]);
      
      // Combine: Same vendor first (box 0 priority), then all vendors
      // Filter out null values (products without images)
      const transformedProducts = {
        top: [
          ...topsSameVendor.map(p => this.transformStorefrontProduct(p, 'top')),
          ...topsAll.map(p => this.transformStorefrontProduct(p, 'top'))
        ].filter(Boolean),
        outerwear: [
          ...outerwearSameVendor.map(p => this.transformStorefrontProduct(p, 'outerwear')),
          ...outerwearAll.map(p => this.transformStorefrontProduct(p, 'outerwear'))
        ].filter(Boolean),
        bottom: [
          ...bottomsSameVendor.map(p => this.transformStorefrontProduct(p, 'bottom')),
          ...bottomsAll.map(p => this.transformStorefrontProduct(p, 'bottom'))
        ].filter(Boolean),
        dress: [
          ...dressesSameVendor.map(p => this.transformStorefrontProduct(p, 'dress')),
          ...dressesAll.map(p => this.transformStorefrontProduct(p, 'dress'))
        ].filter(Boolean),
        handbag: handbags.map(p => this.transformStorefrontProduct(p, 'handbag')).filter(Boolean),
        footwear: footwear.map(p => this.transformStorefrontProduct(p, 'footwear')).filter(Boolean)
      };
      
      // Remove duplicates (same product from both queries)
      this.deduplicateProducts(transformedProducts);
      
      // Store same-vendor counts (before merge, after dedup) for box 0 offset limiting
      this.sameVendorCounts = {
        top: Math.min(topsSameVendor.length, transformedProducts.top.length),
        outerwear: Math.min(outerwearSameVendor.length, transformedProducts.outerwear.length),
        bottom: Math.min(bottomsSameVendor.length, transformedProducts.bottom.length),
        dress: Math.min(dressesSameVendor.length, transformedProducts.dress.length)
      };
      
      // Merge with existing fallback data (prioritize API results)
      this.mergeStorefrontProducts(transformedProducts);
      
      // Re-render with new products
      this.renderProducts();
      
    } catch (error) {
      console.error('❌ Storefront API: Enhancement failed, keeping fallback data:', error);
      // Fallback data is already loaded - no action needed
    }
  }

  /**
   * Remove duplicate products from categorized lists
   */
  deduplicateProducts(categorizedProducts) {
    Object.keys(categorizedProducts).forEach(category => {
      const products = categorizedProducts[category];
      const seen = new Set();
      categorizedProducts[category] = products.filter(p => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
    });
  }

  /**
   * Build GraphQL query for a specific category
   */
  buildStorefrontQuery(category, vendor, limit = 50) {
    // Map our categories to Shopify tags/collections
    // Use escaped quotes for tag values
    const categoryQueries = {
      'tops': 'tag:\\"tops - all\\"',
      'outerwear': '(tag:\\"sweaters - all\\" OR tag:\\"jackets and outerwear - all\\")',
      'bottoms': '(tag:\\"bottoms - all\\" OR tag:\\"denim - jeans\\")',
      'dresses': 'tag:\\"dresses - all\\"',
      'handbags': '(tag:HANDBAGS OR product_type:Handbag OR product_type:\\"Handbags\\") AND NOT tag:\\"handbags - bag accessories\\"',
      'footwear': '(tag:FOOTWEAR OR product_type:Footwear OR product_type:Shoes)'
    };
    
    const categoryQuery = categoryQueries[category] || '';
    const vendorQuery = vendor ? ` AND vendor:\\"${vendor}\\"` : '';
    const availableQuery = ' AND available:true';
    const saleQuery = this.excludeSaleProducts ? ' AND NOT tag:Sale' : '';
    
    const query = `${categoryQuery}${vendorQuery}${availableQuery}${saleQuery}`;
    
    return `{
      products(first: ${limit}, query: "${query}") {
        edges {
          node {
            id
            handle
            title
            vendor
            availableForSale
            onlineStoreUrl
            featuredImage {
              url
              altText
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            tags
          }
        }
      }
    }`;
  }

  /**
   * Query Shopify Storefront API
   */
  async queryStorefrontAPI(category, vendor) {
    try {
      const query = this.buildStorefrontQuery(category, vendor);
      
      const response = await fetch(`https://${window.Shopify.shop}/api/${this.STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.STOREFRONT_API_TOKEN
        },
        body: JSON.stringify({ query })
      });
      
      if (!response.ok) {
        throw new Error(`Storefront API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.errors) {
        console.error(`❌ Storefront API GraphQL errors for ${category}:`, JSON.stringify(data.errors, null, 2));
        console.error('Query was:', query);
        return [];
      }
      
      return data.data?.products?.edges?.map(edge => edge.node) || [];
      
    } catch (error) {
      console.error(`❌ Storefront API: Failed to fetch ${category}:`, error);
      return [];
    }
  }

  /**
   * Transform Storefront API product to our internal format
   * Returns null if product is invalid (no image)
   */
  transformStorefrontProduct(product, category) {
    const price = product.priceRange?.minVariantPrice?.amount || '0';
    const currencyCode = product.priceRange?.minVariantPrice?.currencyCode || 'USD';
    const imageUrl = product.featuredImage?.url || '';
    
    // Skip products not published to Online Store (would result in 404)
    if (!product.onlineStoreUrl) {
      return null;
    }
    
    // Skip out-of-stock products (availableForSale is explicitly false)
    if (product.availableForSale === false) {
      return null;
    }
    
    // Skip products without images (they won't display properly)
    if (!imageUrl) {
      return null;
    }
    
    // Skip sale products if setting is enabled
    if (this.excludeSaleProducts && product.tags?.includes('Sale')) {
      return null;
    }
    
    return {
      id: product.id.replace('gid://shopify/Product/', ''),
      handle: product.handle,
      title: product.title || 'Untitled Product',
      price: `$${parseFloat(price).toFixed(2)}`,
      image: imageUrl,
      imageAlt: product.featuredImage?.altText || product.title || '',
      url: product.onlineStoreUrl, // Use actual Online Store URL (guaranteed valid)
      vendor: product.vendor || '',
      category: category,
      tags: product.tags?.join(',') || '',
      collections: []
    };
  }

  /**
   * Merge Storefront API products with existing categorized products
   */
  mergeStorefrontProducts(storefrontProducts) {
    // Get existing products (includes fallback, will include Product Recs if it runs later)
    const existingProducts = this.categorizedProducts || {};
    
    // Store Storefront products separately (Product Recs API will merge these later if it runs)
    this.storefrontProducts = storefrontProducts;
    
    // Merge: Existing (fallback) first, then Storefront variety
    // Product Recommendations API will add its products to the front later
    const mergedProducts = {
      top: [...(existingProducts.top || []), ...(storefrontProducts.top || [])],
      outerwear: [...(existingProducts.outerwear || []), ...(storefrontProducts.outerwear || [])],
      bottom: [...(existingProducts.bottom || []), ...(storefrontProducts.bottom || [])],
      dress: [...(existingProducts.dress || []), ...(storefrontProducts.dress || [])],
      handbag: [...(existingProducts.handbag || []), ...(storefrontProducts.handbag || [])],
      footwear: [...(existingProducts.footwear || []), ...(storefrontProducts.footwear || [])]
    };
    
    // Deduplicate by product ID
    Object.keys(mergedProducts).forEach(category => {
      const seen = new Set();
      mergedProducts[category] = mergedProducts[category].filter(product => {
        if (seen.has(product.id)) return false;
        seen.add(product.id);
        return true;
      });
    });
    
    this.categorizedProducts = mergedProducts;
    
    // Re-add current product to its category (keep it at the front)
    this.addCurrentProductToCategory();
  }

  // ============================================================================
  // ANALYTICS TRACKING METHODS
  // ============================================================================

  /**
   * Track when a look is viewed (popup opens)
   */
  trackLookView(boxIndex, products) {
    // Fire GA4 event if gtag is available
    if (typeof gtag !== 'undefined') {
      gtag('event', 'view_item_list', {
        item_list_id: 'shop_this_look',
        item_list_name: 'Shop This Look',
        items: products.map((product, idx) => ({
          item_id: product.id,
          item_name: product.title,
          item_brand: product.vendor,
          price: parseFloat(product.price.replace(/[^0-9.]/g, '')),
          index: idx,
          item_list_name: `Shop This Look - Box ${boxIndex + 1}`
        }))
      });
    }
  }

  /**
   * Track when a product is clicked
   */
  trackProductClick(event, element, boxIndex) {
    const productId = element.dataset.productId;
    const productTitle = element.dataset.productTitle;
    const productVendor = element.dataset.productVendor;
    const productPrice = element.dataset.productPrice;
    const position = element.dataset.position;
    
    // Fire GA4 event if gtag is available
    if (typeof gtag !== 'undefined') {
      gtag('event', 'select_item', {
        item_list_id: 'shop_this_look',
        item_list_name: `Shop This Look - Box ${boxIndex + 1}`,
        items: [{
          item_id: productId,
          item_name: productTitle,
          item_brand: productVendor,
          price: parseFloat(productPrice.replace(/[^0-9.]/g, '')),
          index: parseInt(position)
        }]
      });
    }
    
    // Don't prevent default - let link navigate normally
    return true;
  }
}

// Register the custom element
if (!customElements.get('shop-this-look')) {
  customElements.define('shop-this-look', ShopThisLook);
}

// Store global reference for onclick handlers
document.addEventListener('DOMContentLoaded', () => {
  const shopThisLookElement = document.querySelector('shop-this-look');
  if (shopThisLookElement) {
    window.shopThisLookInstance = shopThisLookElement;
  }
});

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  // Store original button text for reset functionality
  document.querySelectorAll('.shop-this-look__cta-button').forEach(button => {
    button.dataset.originalText = button.textContent;
  });
});